import type { TeamsUsageFilter } from './teams-mock-data';

export const PAGE_SIZE = 10;
// Same threshold the credits bar already uses to turn its percentage amber.
const NEAR_LIMIT_PERCENT = 80;
const MAX_WINDOW_SIZE = 500;

export type SeatHolder = { userId: string; used: number; included: number; tokens: number };

export type UsageCounts = Record<TeamsUsageFilter, number>;

export type PagePosition = {
  page: number;
  pageCount: number;
  totalCount: number;
  /** Place of the page's first row in the whole list. */
  start: number;
  /** True when some people cannot be listed, so the pages stop before the end. */
  isTruncated: boolean;
};

export type UsagePagePlan = PagePosition & {
  /** Seat holders on this page, asked for by id. */
  seatIds: string[];
  /** The people without a seat on this page, or null when the page has none. */
  others: { windowSize: number; from: number; count: number } | null;
};

export const isNearLimit = ({ used, included }: Pick<SeatHolder, 'used' | 'included'>) =>
  included > 0 && (used / included) * 100 >= NEAR_LIMIT_PERCENT;

/** Seat holders, the most credits used first. */
export const rankSeatHolders = (holders: SeatHolder[]) =>
  [...holders].sort((a, b) => b.used - a.used);

function seatHoldersFor(holders: SeatHolder[], filter: TeamsUsageFilter) {
  if (filter === 'at_or_near_limit') return holders.filter(isNearLimit);
  if (filter === 'not_using') return holders.filter((holder) => holder.tokens === 0);
  return holders;
}

/**
 * Seat holders are known exactly. Of the people without a seat only the total is known, so those
 * who used nothing are the ones left after taking away everyone seen in the token figures.
 */
export function usageCounts(
  holders: SeatHolder[],
  totalUsers: number,
  tokenUsersWithoutSeat: number
): UsageCounts {
  const idleWithoutSeat = Math.max(0, totalUsers - holders.length - tokenUsersWithoutSeat);
  return {
    all: totalUsers,
    at_or_near_limit: seatHoldersFor(holders, 'at_or_near_limit').length,
    not_using: seatHoldersFor(holders, 'not_using').length + idleWithoutSeat,
  };
}

/** A search is paged by IAM itself, so only the total it reports is needed. */
export const searchPosition = (page: number, totalCount: number): PagePosition => ({
  page,
  pageCount: Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
  totalCount,
  start: page * PAGE_SIZE,
  isTruncated: false,
});

/**
 * The list is the seat holders (most credits first), then everyone else, cut into pages of ten
 * with no gaps. The seat holders on a page are asked for by id. The others are asked for as the
 * first people in IAM's order, enough to step over every seat holder, and the page takes the
 * ones it needs from what is left.
 */
export function planUsagePage(
  holders: SeatHolder[],
  filter: TeamsUsageFilter,
  page: number,
  counts: UsageCounts,
  tokenUsersWithoutSeat: number
): UsagePagePlan {
  const seats = seatHoldersFor(holders, filter);
  const totalCount = Math.max(counts[filter], seats.length);
  const stepOver = holders.length + (filter === 'not_using' ? tokenUsersWithoutSeat : 0);
  // One request holds at most MAX_WINDOW_SIZE people, and the ones stepped over take room in it.
  const reachableOthers = Math.min(totalCount - seats.length, Math.max(0, MAX_WINDOW_SIZE - stepOver));
  const reachableCount = seats.length + reachableOthers;

  const pageCount = Math.max(1, Math.ceil(reachableCount / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const seatIds = seats.slice(start, start + PAGE_SIZE).map((holder) => holder.userId);
  const isTruncated = reachableCount < totalCount;
  const position = { page: safePage, pageCount, totalCount, start, isTruncated, seatIds };

  const count = Math.min(PAGE_SIZE, reachableCount - start) - seatIds.length;
  if (count <= 0) return { ...position, others: null };

  const from = Math.max(0, start - seats.length);
  return { ...position, others: { windowSize: stepOver + from + count, from, count } };
}
